import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import useTenantFromSubdomain, { buildTenantUrl } from "./useTenantFromSubdomain";
import { getStoredIsSuperAdmin } from "../constants/roles";
import { clearFilterState } from "../utils/session";
import { confirmDiscardPendingOfflineData } from "../utils/offlineSync";

// Mirrors useGhostUser.js's structure (pendingX/confirmX/hard-reload pattern)
// since switching tenant is the same class of session-wide state change as
// switching identity via ghost mode. Self-contained (localStorage + local
// state only, no TenantContext dependency) so it can be dropped into
// Usersbar.js/AccountSetting.js without mounting a new provider at the app root.
const useTenantSwitcher = () => {
  const { REACT_APP_BASE_URL } = process.env;
  const tenantSubdomain = useTenantFromSubdomain();
  const [availableTenants, setAvailableTenants] = useState([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [pendingTenant, setPendingTenant] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const fetchAvailableTenants = useCallback(async () => {
    if (!getStoredIsSuperAdmin()) {
      setAvailableTenants([]);
      return [];
    }
    setIsLoadingTenants(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${REACT_APP_BASE_URL}/auth/my-tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tenants = response.data.availableTenants || [];
      setAvailableTenants(tenants);
      return tenants;
    } catch (error) {
      console.error("Error fetching available tenants:", error);
      return [];
    } finally {
      setIsLoadingTenants(false);
    }
  }, [REACT_APP_BASE_URL]);

  useEffect(() => {
    fetchAvailableTenants();
  }, [fetchAvailableTenants]);

  const initiateTenantSwitch = useCallback((tenant) => {
    setPendingTenant(tenant);
    setShowConfirmModal(true);
  }, []);

  const cancelTenantSwitch = useCallback(() => {
    setShowConfirmModal(false);
    setPendingTenant(null);
  }, []);

  const confirmTenantSwitch = useCallback(async () => {
    if (!pendingTenant) return;
    if (!(await confirmDiscardPendingOfflineData("switch tenant"))) return;
    setIsSwitching(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${REACT_APP_BASE_URL}/auth/switch-tenant`,
        { tenantId: pendingTenant.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      const targetSubdomain = data.tenant?.subdomain;

      if (targetSubdomain && targetSubdomain !== tenantSubdomain) {
        // Abandoning this origin entirely — no point writing localStorage or
        // clearing caches here, none of it is reachable from the target subdomain.
        const params = new URLSearchParams({
          token: data.token,
          refreshToken: data.refreshToken || "",
          email: data.email || "",
          roles: data.roles || "",
          userId: data.id || "",
          hubspotUserId: data.hubspot_user_id || "",
          activeTenantId: data.activeTenantId ?? "",
          isSuperAdmin: data.isSuperAdmin ? "true" : "false",
        });
        setShowConfirmModal(false);
        setPendingTenant(null);
        toast.success(`Switching to ${data.tenant?.name || "selected tenant"}…`);
        window.location.replace(`${buildTenantUrl(targetSubdomain, "/auth/cross-login")}#${params.toString()}`);
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("refreshToken", data.refreshToken);
      localStorage.setItem("urserMail", data.email);
      localStorage.setItem("userRole", data.roles);
      localStorage.setItem("userId", data.id);
      localStorage.setItem("currentTenantId", String(data.activeTenantId));
      localStorage.setItem("isSuperAdmin", data.isSuperAdmin ? "true" : "false");

      // Tenant-scoped caches follow in Task F4 (TeamDB etc.); clear the
      // general-purpose caches that already exist today so a switch never
      // shows stale data from the previous tenant, same as ghost mode does.
      sessionStorage.clear();
      localStorage.removeItem("offlineSubmissions");
      clearFilterState();
      try {
        if ("databases" in indexedDB) {
          const dbs = await indexedDB.databases();
          await Promise.all(
            dbs.map((db) => (db.name ? indexedDB.deleteDatabase(db.name) : null))
          );
        }
      } catch (error) {
        console.error("Error clearing IndexedDB databases on tenant switch:", error);
      }

      setShowConfirmModal(false);
      setPendingTenant(null);

      toast.success(`Switched to ${data.tenant?.name || "selected tenant"}`);
      setTimeout(() => {
        const url = new URL(window.location.origin + "/dashboard");
        url.searchParams.set("_reload", Date.now());
        window.location.replace(url.toString());
      }, 1000);
    } catch (error) {
      console.error("Error switching tenant:", error);
      toast.error(error.response?.data?.message || "Failed to switch tenant");
      setShowConfirmModal(false);
      setPendingTenant(null);
    } finally {
      setIsSwitching(false);
    }
  }, [pendingTenant, REACT_APP_BASE_URL]);

  return {
    availableTenants,
    isLoadingTenants,
    pendingTenant,
    showConfirmModal,
    isSwitching,
    fetchAvailableTenants,
    initiateTenantSwitch,
    cancelTenantSwitch,
    confirmTenantSwitch,
  };
};

export default useTenantSwitcher;
